/**
 * uploadErrors – structured upload error types + Swedish UX mapping.
 *
 * Every upload failure is classified into an UploadErrorKind which maps
 * to a user-friendly Swedish message. This guarantees no silent failures.
 */

export type UploadErrorKind =
  | 'format'
  | 'heic_unsupported'
  | 'size'
  | 'auth'
  | 'network'
  | 'timeout'
  | 'storage'
  | 'rls'
  | 'canvas'
  | 'aborted'
  | 'unknown';

export type UploadStage =
  | 'validate'
  | 'compress'
  | 'process'
  | 'upload_storage'
  | 'db_insert'
  | 'finalize'
  | 'done';

export interface StructuredUploadError {
  kind: UploadErrorKind;
  stage: UploadStage;
  message_sv: string;
  originalMessage?: string;
}

const MESSAGE_MAP: Record<UploadErrorKind, string> = {
  format: 'Bildformatet stöds inte. Välj en JPG, PNG eller WebP.',
  heic_unsupported:
    'Bilden är i HEIC-format som inte stöds i webbläsaren. Välj en annan bild eller ändra kamerainställning till JPEG.',
  size: 'Bilden är för stor. Välj en mindre bild (max 15 MB).',
  auth: 'Behörighet saknas för att ladda upp bild.',
  network: 'Nätverksproblem. Kontrollera uppkopplingen och försök igen.',
  timeout: 'Uppladdningen tog för lång tid. Försök igen.',
  storage: 'Kunde inte spara bilden. Försök igen.',
  rls: 'Behörighet saknas för att ladda upp bild.',
  canvas: 'Kunde inte bearbeta bilden i webbläsaren.',
  aborted: 'Uppladdningen avbröts.',
  unknown: 'Kunde inte ladda upp bilden. Försök igen.',
};

export function getUploadErrorMessage(kind: UploadErrorKind): string {
  return MESSAGE_MAP[kind] ?? MESSAGE_MAP.unknown;
}

/** Extract a human-readable message from any error shape (Error, Supabase obj, string, etc.) */
function extractErrorMessage(error: unknown): string {
  if (!error) return 'unknown';
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (typeof error === 'object') {
    const obj = error as Record<string, unknown>;
    // Supabase / PostgREST errors have .message or .error_description
    if (typeof obj.message === 'string') return obj.message;
    if (typeof obj.error_description === 'string') return obj.error_description;
    if (typeof obj.error === 'string') return obj.error;
    try { return JSON.stringify(error); } catch { /* fall through */ }
  }
  return String(error);
}


/**
 * Classify a raw error into an UploadErrorKind based on message heuristics.
 */
export function classifyUploadError(error: unknown): UploadErrorKind {
  if (!error) return 'unknown';
  const msg = extractErrorMessage(error).toLowerCase();

  if (msg.includes('heic') || msg.includes('heif')) return 'heic_unsupported';
  if (msg.includes('stöds inte') || msg.includes('filtyp') || msg.includes('mime')) return 'format';
  if (msg.includes('för stor') || msg.includes('too_large') || msg.includes('max')) return 'size';
  if (msg.includes('inloggad') || msg.includes('auth') || msg.includes('401')) return 'auth';
  if (msg.includes('403') || msg.includes('rls') || msg.includes('policy')) return 'rls';
  if (msg.includes('timeout') || msg.includes('abortcontroller') || msg.includes('signal')) return 'timeout';
  if (msg.includes('abort') || msg.includes('cancel')) return 'aborted';
  if (msg.includes('network') || msg.includes('fetch') || msg.includes('failed to fetch') || msg.includes('load failed')) return 'network';
  if (msg.includes('storage') || msg.includes('bucket')) return 'storage';
  if (msg.includes('canvas')) return 'canvas';

  return 'unknown';
}

/**
 * Build a StructuredUploadError from a raw error + stage.
 */
export function toStructuredUploadError(
  error: unknown,
  stage: UploadStage,
): StructuredUploadError {
  const kind = classifyUploadError(error);
  return {
    kind,
    stage,
    message_sv: getUploadErrorMessage(kind),
    originalMessage: extractErrorMessage(error),
  };
}

/**
 * Detect if a File is HEIC/HEIF based on type or extension.
 */
export function isHeicFile(file: File): boolean {
  const type = (file.type || '').toLowerCase();
  if (type.includes('heic') || type.includes('heif')) return true;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return ext === 'heic' || ext === 'heif';
}
